import React, { useEffect } from "react";
import { Redirect } from "expo-router";
import { MaterialIcons } from "@expo/vector-icons";
import { Tabs } from "expo-router/tabs";

import {
  setupNotificationAfterSigningIn,
  useNotificationRedirectHandler,
} from "@/hooks/notifications";
import { useSession } from "@/context/auth/useSession";
import { useLocationPermission } from "@/hooks/location";
import MessagingInboxIcon from "@/Components/Icons/MessageInboxIcon";
import LoadingScreen from "@/screens/Loading/LoadingScreen";
import { useChatClient } from "@/context/messaging/useChatClient";
import {
  type ChatComponentProps,
  PlaceholderChat,
} from "@/schemas/messaging/stream";
import { isMobileApp } from "@/utils/platform";
import { preLoadImagesOnStartup } from "@/services/user/feed/api";
import { logDeviceData } from "@/hooks/device";
import AccountAvatar from "@/Components/Icons/AccountAvatar";
import { setupRevenueCat } from "@/hooks/revenueCat";
import { isOnboardingCompleted } from "@/utils/onboarding";
import OnboardingScreen from "@/screens/Onboarding/OnboardingScreen";
import { useUpdateInfo } from "@/screens/Settings/Troubleshooting/Services/utils/fetchUpdateInfo";
import { isMarketingMode, isPublicVariant } from "@/utils/expo";

let OverlayProviderComponent = React.Fragment;
let ChatComponent: React.FC<ChatComponentProps> = PlaceholderChat;

if (isMobileApp()) {
  const { Chat, OverlayProvider } = require("stream-chat-expo"); // eslint-disable-line @typescript-eslint/no-var-requires
  OverlayProviderComponent = OverlayProvider;
  ChatComponent = Chat;
}

export default function AppLayout(): React.JSX.Element {
  const { session, isSessionLoading } = useSession();
  const { chatClient, clientIsReady, unreadCount } = useChatClient();
  const { location, requestLocationPermission } = useLocationPermission();
  const updateInfo = useUpdateInfo();

  useNotificationRedirectHandler(session);

  useEffect(() => {
    if (session) {
      // Only run these setup tasks when session is available
      setupNotificationAfterSigningIn(session);
      requestLocationPermission(session);
      preLoadImagesOnStartup(session);
      setupRevenueCat(session);
      logDeviceData({ location, updates: updateInfo });
    }
  }, [session, location]);

  if (isSessionLoading) {
    if (session === null) {
      return <LoadingScreen />;
    }
  } else {
    if (session === null) {
      return <Redirect href="/sign-in" />;
    }
  }

  if (!clientIsReady || chatClient === null || chatClient === undefined) {
    return <LoadingScreen />;
  }

  if (
    // !isPublicVariant() &&
    !isOnboardingCompleted(session.identity.onboardingStage)
  ) {
    // Display onboarding screen for users who haven't completed onboarding
    return (
      <OverlayProviderComponent>
        <OnboardingScreen session={session} />
      </OverlayProviderComponent>
    );
  } else {
    // Display tab navigation for onboarding completed users
    return (
      <OverlayProviderComponent>
        <ChatComponent client={chatClient}>
          <Tabs
            screenOptions={{
              tabBarActiveTintColor: "#000000",
              tabBarShowLabel: false,
            }}
          >
            <Tabs.Screen
              name="explore"
              options={{
                headerShown: false,
                tabBarButtonTestID: "nav-explore-tab",
                href: "/(main)/explore/home",
                tabBarIcon: ({ color, size }) => (
                  <MaterialIcons
                    testID="nav-explore-icon"
                    name="auto-awesome"
                    color={color}
                    size={size}
                  />
                ),
              }}
            />
            <Tabs.Screen
              name="explore-dev"
              options={{
                headerShown: false,
                href:
                  isPublicVariant() || isMarketingMode()
                    ? null
                    : "/(main)/explore-dev/home", // feature flag for explore-dev
                tabBarIcon: ({ color, size }) => (
                  <MaterialIcons name="logo-dev" color={color} size={size} />
                ),
              }}
            />
            <Tabs.Screen
              name="events"
              options={{
                headerShown: false,
                tabBarButtonTestID: "nav-events-tab",
                headerTitle: "events",
                href: null, // @deprecated; setting the href to null for hide the tab.
                tabBarIcon: ({ color, size }) => {
                  return (
                    <MaterialIcons name="event" size={size} color={color} />
                  );
                },
              }}
            />
            <Tabs.Screen
              name="media"
              options={{
                headerShown: false,
                tabBarButtonTestID: "nav-media-tab",
                href: "/(main)/media/home",
                tabBarIcon: ({ color, size }) => (
                  <MaterialIcons
                    name="photo-camera"
                    color={color}
                    size={size}
                  />
                ),
              }}
            />
            <Tabs.Screen
              name="messaging"
              options={{
                headerShown: false,
                tabBarButtonTestID: "nav-messaging-tab",
                href: "/(main)/messaging/home",
                tabBarIcon: ({ color, size }) => (
                  <MessagingInboxIcon
                    unreadCount={unreadCount}
                    color={color}
                    size={size}
                  />
                ),
              }}
            />
            <Tabs.Screen
              name="account"
              options={{
                tabBarButtonTestID: "nav-account-tab",
                headerShown: false,
                href: "/(main)/account/home",
                tabBarIcon: ({ color, size }) => (
                  <AccountAvatar
                    color={color}
                    size={size}
                    avatar={session.identity.avatar}
                    unreadNotificationsCount={
                      session.identity.unreadNotificationsCount
                    }
                  />
                ),
              }}
            />
            <Tabs.Screen
              name="shared"
              options={{
                headerShown: false,
                href: null,
              }}
            />
          </Tabs>
        </ChatComponent>
      </OverlayProviderComponent>
    );
  }
}
